'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { useI18n } from '@/lib/i18n';

/**
 * In-app replacement for the native `window.confirm()` dialog.
 *
 * Use it imperatively from anywhere:
 *   if (await confirmDialog({ message: 'Cancel this appointment?' })) cancel.mutate();
 *
 * A single `<ConfirmHost />` mounted in `Providers.tsx` listens for invocations
 * and renders the dialog. Resolves `true` on confirm, `false` on cancel/escape/
 * backdrop click. Safe under React strict mode (no double-resolution).
 */

interface ConfirmOptions {
  /** Big bold title. Defaults to "Are you sure?" */
  title?: string;
  /** Body copy under the title. */
  message: string;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Colour the primary action red — for destructive ops (delete, decline). */
  destructive?: boolean;
  /** Visual theme; falls back to provider tone if unspecified. */
  theme?: 'customer' | 'provider';
  /** Alert mode — hide the Cancel button (single acknowledge action). */
  hideCancel?: boolean;
}

type Pending = ConfirmOptions & { resolve: (value: boolean) => void };

// Module-level subscriber: the host registers; `confirmDialog()` invokes it.
let subscriber: ((p: Pending) => void) | null = null;

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  if (!subscriber) {
    // The host isn't mounted (SSR or unit test) — degrade gracefully so the
    // caller still gets a sensible boolean.
    if (typeof window !== 'undefined') return Promise.resolve(window.confirm(opts.message));
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    subscriber!({ ...opts, resolve });
  });
}

/** Mount once near the app root. Renders the actual dialog when triggered. */
export function ConfirmHost() {
  const { t } = useI18n();
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    subscriber = (p) => setPending(p);
    return () => { subscriber = null; };
  }, []);

  useEffect(() => {
    if (!pending) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') resolve(false);
      if (e.key === 'Enter') resolve(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  function resolve(value: boolean) {
    if (!pending) return;
    pending.resolve(value);
    setPending(null);
  }

  if (!pending) return null;
  const theme = pending.theme ?? 'provider';
  const isDestructive = pending.destructive;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
      onClick={() => resolve(false)}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        className="w-full max-w-sm rounded-3xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex items-start gap-3">
          <span className={cn(
            'w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0',
            isDestructive ? 'bg-red-50 text-red-500'
              : theme === 'customer' ? 'bg-customer-50 text-customer-500' : 'bg-provider-50 text-provider-500',
          )}>
            <AlertTriangle size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900 text-base">{pending.title ?? t('confirm.defaultTitle')}</h3>
            <p className="text-sm text-gray-600 mt-1 leading-snug">{pending.message}</p>
          </div>
        </div>
        <div className="px-6 pb-5 pt-1 flex gap-2 justify-end">
          {!pending.hideCancel && (
            <Button
              theme={theme}
              variant="secondary"
              type="button"
              onClick={() => resolve(false)}
            >
              {pending.cancelLabel ?? t('common.cancel')}
            </Button>
          )}
          <Button
            theme={theme}
            type="button"
            onClick={() => resolve(true)}
            className={isDestructive ? '!bg-red-500 hover:!bg-red-600' : ''}
          >
            {pending.confirmLabel ?? t('common.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
